import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Parameters:
// -- query: an object containing key-value pairs that will
//           be set as URL search params
// -- initFromURL: a function that will initialize any component
//           data based on existing URL search params

export const useQuery = (
  query: { [key: string]: string | null | undefined },
  initFromURL: (URLSearchParams) => void
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Prevents race condition and infinite looping useEffect:
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    let shouldInit = false;

    Object.keys(query).forEach((key) => {
      if (searchParams.get(key)) {
        shouldInit = true;
      }
    });

    if (shouldInit) {
      initFromURL(searchParams);
    } else {
      // If URL search params are empty, we don't need to initialize
      setInitialized(true);
    }
  }, []);

  useMemo(() => {
    const newParams = new URLSearchParams(searchParams);
    let shouldUpdate = false;

    Object.entries(query).forEach(([key, value]) => {
      if (value && value != newParams.get(key)) {
        shouldUpdate = true;
        newParams.set(key, value);
      } else if (!value && searchParams.get(key)) {
        shouldUpdate = true;
        newParams.delete(key);
      }
    });

    if (shouldUpdate && initialized) {
      setSearchParams(newParams);
    }

    if (!shouldUpdate && !initialized) {
      setInitialized(true);
    }
  }, [query]);
};
